import { getFunctions, httpsCallable } from "firebase/functions";
import { getMessaging, getToken, deleteToken, isSupported } from "firebase/messaging";
import app, { auth, FUNCTIONS_REGION } from "../../firebaseApp";

export type PushPreferences = { confirmations: boolean; reminders: boolean; cancellations: boolean; announcements: boolean; adminSignups?: boolean; adminPayg?: boolean };
export const defaultPreferences: PushPreferences = { confirmations: true, reminders: true, cancellations: true, announcements: false, adminSignups: false, adminPayg: false };
const storageKey = "alphawod.push.device";
const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim();
export const pushConfigured = Boolean(vapidKey);
const manage = httpsCallable<{action: string; token: string; preferences?: PushPreferences}, {preferences?: PushPreferences | null}>(getFunctions(app, FUNCTIONS_REGION), "managePushDevice");
function stored(): {token: string; uid: string} | null {
  try { return JSON.parse(localStorage.getItem(storageKey) || "null"); } catch { return null; }
}
export function needsHomeScreen() {
  return (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) && !window.matchMedia("(display-mode: standalone)").matches && !(navigator as Navigator & {standalone?: boolean}).standalone;
}
export async function loadPushPreferences(): Promise<PushPreferences | null> {
  const device = stored();
  if (!device || device.uid !== auth.currentUser?.uid) return null;
  const result = await manage({action: "get", token: device.token});
  return result.data.preferences || null;
}
export async function enablePush(preferences: PushPreferences) {
  if (!vapidKey) throw new Error("Notifications are not available yet.");
  // Call permission directly in the click handler's stack for Safari's gesture requirement.
  if (!("Notification" in window)) throw new Error("This browser does not support notifications.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications are blocked. Allow them in your device or browser settings, then try again.");
  if (!(await isSupported())) throw new Error("This browser does not support push notifications.");
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sign in to enable notifications.");
  const registration = await navigator.serviceWorker.register("/push-worker.js", {scope: "/"});
  await navigator.serviceWorker.ready;
  const token = await getToken(getMessaging(app), {vapidKey, serviceWorkerRegistration: registration});
  if (auth.currentUser?.uid !== uid) throw new Error("Your account changed. Please try again.");
  await manage({action: "save", token, preferences});
  localStorage.setItem(storageKey, JSON.stringify({token, uid}));
}
export async function disablePush() {
  const device = stored();
  if (!device) return;
  await manage({action: "remove", token: device.token});
  localStorage.removeItem(storageKey);
  // Server removal is authoritative even if browser cleanup fails.
  try { if (await isSupported()) await deleteToken(getMessaging(app)); } catch { /* already unsubscribed on the server */ }
}
export async function savePushPreferences(preferences: PushPreferences) {
  const device = stored();
  if (!device || device.uid !== auth.currentUser?.uid) throw new Error("Enable notifications on this device first.");
  await manage({action: "save", token: device.token, preferences});
}
export async function refreshPushRegistration() {
  const device = stored();
  if (!device || !auth.currentUser) return;
  if (device.uid !== auth.currentUser.uid) {
    // Never transfer an old account's consent to a new account.
    if (await isSupported()) await deleteToken(getMessaging(app));
    localStorage.removeItem(storageKey);
    return;
  }
  if (!("Notification" in window) || Notification.permission !== "granted") { await disablePush(); return; }
  const preferences = await loadPushPreferences();
  if (!preferences || !vapidKey || !(await isSupported())) return;
  const registration = await navigator.serviceWorker.register("/push-worker.js", {scope: "/"});
  const token = await getToken(getMessaging(app), {vapidKey, serviceWorkerRegistration: registration});
  if (auth.currentUser?.uid !== device.uid) return;
  await manage({action: "save", token, preferences});
  if (device.token !== token) await manage({action: "remove", token: device.token});
  localStorage.setItem(storageKey, JSON.stringify({token, uid: device.uid}));
}
export const publishAnnouncement = httpsCallable<{title: string; body: string; requestId: string}, {id: string; queued: boolean}>(getFunctions(app, FUNCTIONS_REGION), "publishPushAnnouncement");

export async function sendTestPush() {
  const device = stored();
  if (!device || device.uid !== auth.currentUser?.uid) throw new Error("Enable notifications on this device first.");
  await manage({action: "test", token: device.token});
}
